<template>
  <div class="d-flex contributor-row" :class="{ flexible: !isFixed }">
    <div
      class="icon kr-icon-wrapper flex-center mr-2 top-shift"
      v-if="entityType === ENTITY_TYPE.KEY_RESULT"
    >
      <i class="flex-shrink-0" :class="icon"></i>
    </div>
    <i class="icon flex-shrink-0 mr-2 top-shift" :class="icon" v-else></i>
    <div class="flex-grow-1 bold top-shift mr-2" v-no-capture>{{ title }}</div>
    <div class="right-section d-flex">
      <div
        class="team-name text-small-1 text-secondary text-truncate"
        v-if="entityType === ENTITY_TYPE.GOAL"
        :title="teamTimelineName"
      >
        {{ teamTimelineName }}
      </div>
      <div class="goal-progress position-relative">
        <input
          type="number"
          max="100"
          min="0"
          class="text-center round"
          :class="{ 'errored-value': isEmpty }"
          :value="contribution"
          @input="onInput"
          @keydown.e.prevent
          id="fieldName"
          autocomplete="off"
          data-prevent-blur="true"
          v-if="isEditing"
          @blur="isEditing = false"
          v-autofocus
        />
        <div
          class="h-100 flex-center read-view-contribution"
          :class="{ 'errored-value': isEmpty }"
          @click="isEditing = true"
          @focus="isEditing = true"
          tabindex="0"
          v-else
        >
          {{ contribution }}&nbsp;

          <span class="fade-in" v-if="!isEmpty">%</span>
        </div>
        <transition name="slide-right">
          <div key="2" class="percentage-symbol" v-if="isEditing">%</div>
        </transition>
        <i
          class="hoverable-lock-icon row-icon pointer"
          v-if="isFixed"
          :id="'lock-icon-' + uuid"
          @click="resetContribution"
        ></i>
        <i
          class="hoverable-lock-grey-icon row-icon pointer"
          :id="'lock-icon-' + uuid"
          @click="lockContribution"
          v-else
        ></i>
        <div class="text-mini text-error position-absolute text-nowrap" v-if="isEmpty">
          This cannot be blank
        </div>
        <popover :target="'lock-icon-' + uuid" triggers="hover" :show-delay="200" :hide-delay="0">
          <div class="text-medium-1">
            <div class="mb-2 d-flex align-items-center">
              <i class="lock-filled-icon mr-2"></i>
              <b v-if="isFixed">Locked Contribution</b>
              <b v-else>Lock this contribution</b>
            </div>
            <div class="thin" v-if="isFixed">
              You have edited and locked this contribution. When new Key Results or Child goals are
              added or removed, locked contributions will not be updated automatically.
              <br />
              Click the lock icon to reset.
            </div>
            <div v-else>
              If you lock this contribution, when new items are added they will not impact this one.
            </div>
          </div>
        </popover>
      </div>
    </div>
  </div>
</template>

<script>
import { trackAnalyticsEvent } from '@/analytics'
import { EVENT_ACTION, EVENT_CATEGORY } from '@/constants/analytics'
import { ENTITY_TYPE } from '@/constants/entities'
import Popover from '@/components/Popover'
import { FLEXIBLE_WEIGHTAGE } from '@/constants/goal'
import { intervalWithId } from '@/interval'
import ComponentIdMixin from '@/mixins/ComponentIdMixin'

/**
 * This is the row item for a contributor. It allows setting of contribution percentage.
 */
export default {
  name: 'GoalContributorRow',
  props: ['entityType', 'entity', 'isFixed', 'contribution', 'openInEditMode', 'source'],
  mixins: [ComponentIdMixin],
  components: {
    Popover
  },
  data() {
    return {
      ENTITY_TYPE,
      isEditing: this.openInEditMode,
      isValueSetEventSent: false
    }
  },
  computed: {
    title() {
      return this.entity.title
    },
    teamTimelineName() {
      const team = this.$store.getters.teamById(this.entity.teamId)
      return `${team.name} - ${this.timeline}`
    },
    timeline() {
      return intervalWithId(this.entity.timeline).name
    },
    icon() {
      if (this.entityType === ENTITY_TYPE.GOAL) {
        return 'goal-icon'
      } else if (this.entityType === ENTITY_TYPE.KEY_RESULT) {
        return 'kr-icon'
      }
      return null
    },
    isEmpty() {
      return this.contribution.toString().length === 0
    }
  },
  methods: {
    onInput(event) {
      const value = event.target.value.match(/(\d*\.?\d\d?)/)?.[0] || '' // restrict max decimals limit to 2
      this.$emit('change', value)
      this.trackValueSetEvent()
    },
    resetContribution() {
      this.$emit('change', FLEXIBLE_WEIGHTAGE)
    },
    lockContribution() {
      this.$emit('change', this.contribution)
    },
    trackValueSetEvent() {
      if (!this.isValueSetEventSent) {
        trackAnalyticsEvent(EVENT_CATEGORY.ACTIVITY, EVENT_ACTION.SET_GOAL_CONTRIBUTIONS, {
          CTA: this.source
        })
        this.isValueSetEventSent = true
      }
    }
  },
  mounted() {}
}
</script>

<style lang="scss" scoped>
@import './styles/goalContributionModal';
@import '~@/styles/_styleConfigurations.scss';
@import '~@/styles/components/_input.scss';

.contributor-row {
  padding: 8px 0;
  min-height: 38px;
  box-sizing: content-box;
}

.kr-icon-wrapper {
  @include dimension(20px, 20px);
  background-color: $brandQuaternary;
  border-radius: 50%;
  flex-shrink: 0;
}

.icon {
  z-index: 1;
}

.goal-icon {
  @include dimension(20px, 20px);
  background-size: contain;
}

.goal-progress {
  position: relative;
  height: 38px;
}

.percentage-symbol {
  position: absolute;
  top: 7px;
  right: 10px;
}

/deep/ input {
  padding: 0 28px 0 12px;
  width: 100px;
  height: 38px;
}

.read-view-contribution {
  border: 1px solid $borderPrimary;
  border-radius: 50px;
}

.row-icon {
  position: absolute;
  right: -22px;
  top: 50%;
  transform: translateY(-50%);
}

@keyframes fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.fade-in {
  animation: fade-in ease-in 200ms;
}

.slide-right-enter-active,
.slide-right-leave-active,
.slide-right-leave-to {
  transition: right 100ms ease-out;
}

.slide-right-enter {
  right: 20px;
}

.slide-right-leave-to {
  right: 20px;
}

input:not(.errored-value):focus {
  border-color: $prime;
}

.errored-value {
  border-color: $error !important;
}

.flexible {
  .row-icon {
    opacity: 0;
    transition: opacity 100ms ease-in;
  }
  &:hover .row-icon {
    opacity: 1;
  }
}
.team-name {
  line-height: 38px;
}

.top-shift {
  margin-top: 7px;
}
</style>
